/**
 * FloatingPanel — a draggable, optionally-resizable, non-modal floating window
 * (the chassis extracted from the Object Viewer). NOT a modal: no backdrop, no
 * portal, no focus trap, so the page behind it stays fully usable. Drag it by the
 * header; it goes semi-transparent ("dim") when you click outside it and opaque
 * again the moment you interact. The header and an optional footer are pinned; the
 * body is a flex column you fill — manage your own scroll inside it (wrap scrollers
 * in `.fp-scroll`, or give a child `flex:1; overflow:auto`).
 *
 * Props:
 *   open        — visibility; the parent owns the signal. Closed → fully unmounted.
 *   onClose     — called by the ✕ button and (unless closeOnEsc={false}) by Esc.
 *   title       — header text or node.
 *   kbd         — optional shortcut-hint chip in the header (e.g. "Ctrl+F12").
 *   width       — width in px (default 430; the live width when resizable).
 *   height      — initial height in px when resizable (default 480).
 *   maxHeight   — CSS max-height for the NON-resizable case (default "74vh").
 *   resizable   — show a bottom-right resize grip and use an explicit height.
 *   initialPos  — { x, y } starting position; default = pinned to the top-right.
 *   onGeometry  — called with { x, y, w, h } after a drag or resize ends (for
 *                 persistence). w/h are meaningful mainly when resizable.
 *   closeOnEsc  — Esc closes the panel (default true).
 *   headerActions — node(s) rendered just left of the ✕ (e.g. a Reattach button).
 *   footer      — optional node rendered in a pinned footer strip.
 *   class       — extra class on the panel root.
 *   children    — the body content.
 */
import { createSignal, onMount, onCleanup, Show } from 'solid-js';

export default function FloatingPanel(props) {
  const [size, setSize] = createSignal({ w: props.width || 430, h: props.height || 480 });
  const w = () => (props.resizable ? size().w : (props.width || 430));
  const defaultPos = () => ({ x: Math.max(16, (window.innerWidth || 1200) - w() - 16), y: 88 });
  const [pos, setPos] = createSignal(props.initialPos || defaultPos());
  const [active, setActive] = createSignal(true); // opacity: opaque while interacting
  let panelRef;

  const emitGeometry = () => props.onGeometry?.({ x: pos().x, y: pos().y, w: size().w, h: size().h });

  // Dim when a pointerdown lands outside the panel; opaque again on interaction.
  // Capture phase so it beats inner handlers.
  onMount(() => {
    const onDocDown = (e) => { if (props.open && panelRef && !panelRef.contains(e.target)) setActive(false); };
    document.addEventListener('pointerdown', onDocDown, true);
    onCleanup(() => document.removeEventListener('pointerdown', onDocDown, true));
  });

  // Esc to close (opt-out with closeOnEsc={false}). Capture + stopPropagation so it
  // doesn't also trigger a global Esc handler (e.g. focus-mode exit) behind it.
  onMount(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && props.open && props.closeOnEsc !== false) { e.stopPropagation(); props.onClose?.(); }
    };
    document.addEventListener('keydown', onKey, true);
    onCleanup(() => document.removeEventListener('keydown', onKey, true));
  });

  // Drag by the header. Listeners go on `window` (not the small header) so a fast
  // drag that outruns the cursor keeps tracking.
  let dragStart = null;
  const onMove = (e) => {
    if (!dragStart) return;
    setPos({ x: dragStart.px + (e.clientX - dragStart.x), y: Math.max(0, dragStart.py + (e.clientY - dragStart.y)) });
  };
  const onUp = () => {
    if (dragStart) { dragStart = null; emitGeometry(); }
    window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp);
  };
  const startDrag = (e) => {
    // Don't start a drag from an interactive header control (close button, etc.).
    if (e.target.closest('button, select, input, a')) return;
    setActive(true);
    dragStart = { x: e.clientX, y: e.clientY, px: pos().x, py: pos().y };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  onCleanup(onUp);

  // Resize from the bottom-right grip (resizable panels only).
  let resizeStart = null;
  const onResizeMove = (e) => {
    if (!resizeStart) return;
    setSize({
      w: Math.max(280, resizeStart.w + (e.clientX - resizeStart.x)),
      h: Math.max(180, resizeStart.h + (e.clientY - resizeStart.y)),
    });
  };
  const onResizeUp = () => {
    if (resizeStart) { resizeStart = null; emitGeometry(); }
    window.removeEventListener('pointermove', onResizeMove); window.removeEventListener('pointerup', onResizeUp);
  };
  const startResize = (e) => {
    e.preventDefault(); e.stopPropagation();
    setActive(true);
    resizeStart = { x: e.clientX, y: e.clientY, w: size().w, h: size().h };
    window.addEventListener('pointermove', onResizeMove);
    window.addEventListener('pointerup', onResizeUp);
  };
  onCleanup(onResizeUp);

  const styleStr = () => {
    const base = `left:${pos().x}px;top:${pos().y}px;width:${w()}px;`;
    return props.resizable ? base + `height:${size().h}px;` : base + `max-height:${props.maxHeight || '74vh'};`;
  };

  return (
    <Show when={props.open}>
      <div class={`fp-panel ${props.class || ''}`} ref={panelRef} classList={{ 'fp-dim': !active() }}
           style={styleStr()} onPointerDown={() => setActive(true)}>
        <div class="fp-head" onPointerDown={startDrag}>
          <span class="fp-title">{props.title}</span>
          <Show when={props.kbd}><span class="fp-kbd">{props.kbd}</span></Show>
          <span class="spacer" />
          {props.headerActions}
          <button class="fp-x" title="Close" onClick={() => props.onClose?.()}>×</button>
        </div>
        <div class="fp-body">{props.children}</div>
        <Show when={props.footer}>
          <div class="fp-foot">{props.footer}</div>
        </Show>
        <Show when={props.resizable}>
          <div class="fp-resize" title="Drag to resize" onPointerDown={startResize} />
        </Show>
      </div>
    </Show>
  );
}
